import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   WIRT 宿主 —— BOSS・寄生
   ============================================================

   剪影：熟悉的四足機甲＋不該長在那的東西
   階段：'P1 機甲正常走・寄生體縮在背上','P2 寄生體張開・一條腿開始拖','P3 寄生體脫離升空・機甲癱在地上'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-21 WIRT 宿主 ── 這隻的恐怖來自「那曾經是我們的」。
   下半身刻意做成玩家已經認得的四足機甲量體（WALZE 的語彙），
   上半身是不該長在那裡的東西。
   P1 機甲正常行走，寄生體縮在背上幾乎看不見 —— 你會以為這是一隻普通 BOSS。
   P2 寄生體張開包住車體，一條腿開始拖行。
   P3 寄生體脫離升空；機甲殘骸癱在地上，不再是威脅，只是留在那裡。 */
export function buildWirt(){
  var GL = glowMat(TINT.red.hex, 1.9), GLV = glowMat(TINT.vio.hex, 2.5);
  var root = new THREE.Group(), legs = [], tent = [], i, k;
  /* ── 宿主：熟悉的機甲量體 ── */
  var host = boneG(root, 0, 2.30, 0);
  host.add(bevel(M.dark,  2.60, 1.00, 3.00, [0,0,0]));
  host.add(plate(M.armor, 2.80, 0.56, 1.50, [0, 0.44, 0.80], -0.34));
  host.add(plate(M.armor, 2.60, 0.50, 1.30, [0, 0.40,-0.86],  0.30));
  host.add(rib  (M.armor, 2.86, 0.12, 0.12, [0, 0.66, 0.36], -0.34));
  host.add(plate(M.armor, 0.34, 0.80, 2.60, [ 1.40,-0.14, 0], 0, -0.12, -0.18));
  host.add(plate(M.armor, 0.34, 0.80, 2.60, [-1.40,-0.14, 0], 0,  0.12,  0.18));
  for (k = 0; k < 4; k++){
    var sx = (k % 2) ? 1 : -1, sz = (k < 2) ? 1 : -1;
    var th = boneG(host, sx*1.30, -0.40, sz*1.10);
    th.add(mesh(G.cyl, M.armor, [0.52,1.50,0.52], [0,-0.75,0]));
    th.add(mesh(G.cyl, M.trim,  [0.60,0.28,0.60], [0,-1.46,0], [0,0,HP]));
    var sh = boneG(th, 0, -1.50, 0);
    sh.add(mesh(G.cyl, M.armor, [0.42,1.40,0.42], [0,-0.70,0]));
    sh.add(mesh(G.box, M.dark,  [0.66,0.26,1.30], [0,-1.42,0.16]));
    var kg = mesh(G.oct, GL, [0.34,0.34,0.34], [0, 0.02,-0.14]);
    kg.userData.noMerge = 1; sh.add(kg);
    legs.push({th:th, sh:sh, kg:kg, k:k});
  }
  /* ── 寄生體：不對稱、沒有面板、沒有接縫 ── */
  var par = boneG(host, 0.30, 0.70, -0.30);
  var pv = mesh(G.oct, M.void, [2.20,2.00,2.20], [0,0,0]);
  pv.userData.noMerge = 1; par.add(pv);
  var pe = mesh(G.oct, GLV, [0.90,0.90,0.90], [0.25, 0.40, 0.55]);
  pe.userData.noMerge = 1; par.add(pe);
  par.add(mesh(G.tor, M.trim, [2.60,2.60,0.34], [0,0,0], [0.4,0.3,0]));
  for (i = 0; i < 6; i++){
    var a = i*Math.PI * 2/6 + 0.31;
    var tb = boneG(par, Math.sin(a)*0.90, -0.10, Math.cos(a)*0.90);
    tb.rotation.y = a;
    tb.add(mesh(G.cone, M.dark, [0.46,2.20,0.46], [0,0,0.90], [HP,0,0]));
    var t2 = boneG(tb, 0, 0, 1.80);
    t2.add(mesh(G.cone, M.dark, [0.30,1.80,0.30], [0,0,0.80], [HP,0,0]));
    var tg = mesh(G.oct, GLV, [0.30,0.30,0.30], [0,0,1.60]);
    tg.userData.noMerge = 1; t2.add(tg);
    tent.push({a:tb, b:t2, i:i});
  }
  root.userData.rig = {host:host, legs:legs, par:par, tent:tent, pv:pv, pe:pe, GL:GL, GLV:GLV};
  return mergeBones(root);
}
export function updWirt(rig, o, dt){
  stages(rig, o, dt, 1.4);
  var s1 = rig.s1, s2 = rig.s2, i, L, T;
  /* 宿主：階段越後越像殘骸 */
  var alive = 1 - s2;
  rig.host.position.y = 2.30 - 0.40*s1 - 1.35*s2 + 0.08*Math.sin(o.ph*2*Math.PI * 2)*o.mv*alive;
  rig.host.rotation.z = 0.16*s1 + 0.42*s2;
  rig.host.rotation.x = 0.06*Math.sin(o.ph*Math.PI * 2)*o.mv*alive - 0.14*s2;
  for (i = 0; i < 4; i++){
    L = rig.legs[i];
    var lame = (i === 0) ? s1 : 0;                  // 二階段左前腿開始拖
    var ph = (o.ph + (i % 3 ? 0.5 : 0)) % 1;
    var sw = Math.sin(ph*Math.PI * 2)*o.mv*alive*(1 - lame);
    L.th.rotation.x = 0.20 + sw*0.42 + lame*0.55 + s2*(0.9 + 0.3*i);
    L.sh.rotation.x = -0.40 - Math.max(0, sw)*0.55 - lame*0.30 - s2*0.8;
    L.kg.scale.setScalar((1 + 0.5*o.tel)*(1 - 0.7*s2));
  }
  /* 寄生體：張開 → 脫離升空 */
  rig.par.position.set(0.30 - 0.30*s2, 0.70 + 1.10*s1 + 4.30*s2, -0.30 + 0.30*s2);
  rig.par.rotation.y += dt*(0.25 + 0.8*s1 + 1.4*s2);
  rig.par.rotation.z = 0.20*Math.sin(o.t*0.7) - 0.16*s1;
  rig.par.scale.setScalar(0.42 + 0.48*s1 + 0.35*s2);
  for (i = 0; i < 6; i++){
    T = rig.tent[i];
    T.a.rotation.x = -0.30 + 1.00*s1 + 0.50*s2 + 0.34*Math.sin(o.t*1.5 + i*1.05)
                   - o.tel*0.34 + o.atk*0.70;
    T.a.rotation.z = 0.28*Math.sin(o.t*1.1 + i*2.1);
    T.b.rotation.x = 0.55 + 0.40*Math.sin(o.t*1.9 + i*1.7) + o.atk*0.60;
  }
  rig.pv.scale.setScalar(1 + 0.10*Math.sin(o.t*2.4) + 0.35*s2);
  rig.pe.scale.setScalar(0.8 + 0.35*Math.sin(o.t*3.1) + 1.0*o.tel + 0.5*s2);
  rig.GL.emissiveIntensity  = 1.9*alive + 2.0*o.tel*alive;
  rig.GLV.emissiveIntensity = 1.2 + 1.8*s1 + 2.6*s2 + 2.6*o.tel;
}